'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { GlobalDesktopSideAdBanner } from '@/app/_components/ads';
import { PageNavigation } from '@/components/client';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { HomeFeedListAdBanner } from '../../(home)/_components/HomeFeedListAdBanner';
import type { HouseholdSize } from '../_hooks/useMedianIncomeState';
import { useMedianIncomeState } from '../_hooks/useMedianIncomeState';
import type { MedianIncomeYear } from '../constants';
import { MEDIAN_INCOME_YEARS } from '../constants';
import { BenefitCards } from './BenefitCards';
import { Calculator } from './Calculator';
import { ConceptGrid } from './ConceptGrid';
import { FaqList } from './FaqList';
import { FinalCta } from './FinalCta';
import { Hero } from './Hero';
import { MobileStickyCalculator } from './MobileStickyCalculator';
import { PolicyCards } from './PolicyCards';
import { RecommendedContents } from './RecommendedContents';
import { TierTable } from './TierTable';
import { YearTabs } from './YearTabs';
interface MedianIncomeSectionProps {
  /** 초기 진입 연도 (SSR에서 결정). */
  initialYear: MedianIncomeYear;
  /** 외부(다른 페이지)에서 진입 시 URL 쿼리로 전달된 초기 계산기 값. */
  initialValues?: {
    householdSize?: HouseholdSize;
    percent?: number;
  };
}

/**
 * `/median-income` 본문 — 연도 상태 + 탭 + 전체 레이아웃.
 *
 * 모든 섹션이 하나의 흰 카드(wrapper) 안에 들어가며, 섹션 간 간격은 `gap-32` 로 띄운다.
 * 모바일 mid 광고만 wrapper 바깥에서 그룹 분리 역할.
 *
 * 연도 탭 클릭 시 `useMedianIncomeState` 가 year 변경을 감지해 월 소득은 유지, percent 만
 * 새 baseline 기준으로 재계산.
 */
export const MedianIncomeSection = t0 => {
  const $ = _c(55);
  const {
    initialYear,
    initialValues
  } = t0;
  const [year, setYear] = useState(initialYear);
  const state = useMedianIncomeState(year, initialValues);
  const {
    householdSize,
    percent,
    tier
  } = state;
  const hasMultipleYears = MEDIAN_INCOME_YEARS.length >= 2;
  const scrollToTierTable = _temp;
  let t1;
  let t2;
  let t3;
  if ($[0] !== state) {
    t1 = next => {
      state.setHouseholdSize(next);
      scrollToTierTable();
    };
    t2 = raw => {
      state.onAmountChange(raw);
      scrollToTierTable();
    };
    t3 = raw_0 => {
      state.onPercentChange(raw_0);
      scrollToTierTable();
    };
    $[0] = state;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== state || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      ...state,
      setHouseholdSize: t1,
      onAmountChange: t2,
      onPercentChange: t3
    };
    $[4] = state;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const calculatorState = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PageNavigation title={"\uC911\uC704\uC18C\uB4DD \uC885\uD569 \uAC00\uC774\uB4DC"} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== calculatorState) {
    t6 = <GlobalDesktopSideAdBanner className="flex flex-col gap-16 self-stretch mobile-tablet:hidden" footer={<div className="sticky top-80"><Calculator state={calculatorState} /></div>} />;
    $[10] = calculatorState;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== year) {
    t7 = hasMultipleYears ? <YearTabs selectedYear={year} onChange={setYear} /> : null;
    $[12] = year;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== year) {
    t8 = <Hero year={year} />;
    $[14] = year;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== state) {
    t9 = tierPercent => state.onPercentChange(String(tierPercent));
    $[16] = state;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== householdSize || $[19] !== t9 || $[20] !== tier || $[21] !== year) {
    t10 = <TierTable year={year} householdSize={householdSize} activeTier={tier} onTierClick={t9} />;
    $[18] = householdSize;
    $[19] = t9;
    $[20] = tier;
    $[21] = year;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== tier || $[24] !== year) {
    t11 = <PolicyCards year={year} activeTier={tier} />;
    $[23] = tier;
    $[24] = year;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="-mx-24 overflow-hidden mobile-tablet:mx-0"><HomeFeedListAdBanner /></div>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== percent || $[28] !== year) {
    t13 = <BenefitCards year={year} percent={percent} />;
    $[27] = percent;
    $[28] = year;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="-mx-24 overflow-hidden mobile-tablet:mx-0"><HomeFeedListAdBanner /></div>;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  let t16;
  if ($[31] !== year) {
    t15 = <ConceptGrid year={year} />;
    t16 = <RecommendedContents year={year} />;
    $[31] = year;
    $[32] = t15;
    $[33] = t16;
  } else {
    t15 = $[32];
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="-mx-24 overflow-hidden mobile-tablet:mx-0"><HomeFeedListAdBanner /></div>;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== year) {
    t18 = <FaqList year={year} />;
    $[35] = year;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FinalCta />;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t13 || $[41] !== t15 || $[42] !== t16 || $[43] !== t18 || $[44] !== t7 || $[45] !== t8) {
    t20 = <div className="flex flex-col gap-32 rounded-medium border-1 border-Gray-200 bg-Gray-white p-24 mobile-tablet:gap-24 mobile-tablet:rounded-none mobile-tablet:border-0 mobile-tablet:px-0 mobile-tablet:py-16">{t7}{t8}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t13;
    $[41] = t15;
    $[42] = t16;
    $[43] = t18;
    $[44] = t7;
    $[45] = t8;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20 || $[48] !== t6) {
    t21 = <div className="bg-Gray-25 py-64 mobile-tablet:bg-Gray-white mobile-tablet:pb-180 mobile-tablet:pt-0"><DesktopPageShell mainClassName="flex flex-col gap-16" rightAside={t6}>{t20}</DesktopPageShell></div>;
    $[47] = t20;
    $[48] = t6;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== calculatorState) {
    t22 = <MobileStickyCalculator state={calculatorState} />;
    $[50] = calculatorState;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t21 || $[53] !== t22) {
    t23 = <>{t5}{t21}{t22}</>;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  return t23;
};
function _temp() {
  document.getElementById("median-income-tier-table")?.scrollIntoView({
    behavior: "smooth",
    block: "start"
  });
}